function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
/* @pareto-engineering/generator-front 1.0.12 */
import * as React from 'react';
import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useCombobox } from 'downshift';
import styleNames from '@pareto-engineering/bem/exports';
import { FormLabel, FormDescription } from "../../../..";
import { Popover, LoadingCircle } from "../../../../../a";
import { Button } from "../../../../../b";
import { lookUpInputValueFromFetchedOptions } from "../../../../common";

// Local Definitions

import { Menu } from "../Menu";
const baseClassName = styleNames.base;
const componentClassName = 'combobox';

/**
 * This is the component description.
 */
const Combobox = ({
  id,
  className: userClassName,
  style,
  label,
  labelColor,
  name,
  options: items,
  getOptions,
  setOptions,
  setValue,
  description,
  value,
  color,
  minLength,
  isFetching,
  transformSearch,
  disabled,
  optional,
  placeholder,
  promptCreateNewOption
  // ...otherProps
}) => {
  const {
    isOpen,
    selectItem,
    selectedItem,
    inputValue,
    setInputValue,
    getLabelProps,
    getMenuProps,
    getInputProps,
    highlightedIndex,
    getComboboxProps,
    getItemProps
  } = useCombobox({
    items,
    initialSelectedItem: value,
    itemToString: item => item ? item.label : '',
    onInputValueChange: ({
      inputValue: searchInputValue
    }) => {
      const transformedInput = transformSearch(searchInputValue);
      if (transformedInput.length > minLength) {
        getOptions(transformedInput);
      }
    }
  });
  useEffect(() => {
    if (promptCreateNewOption) {
      lookUpInputValueFromFetchedOptions({
        items,
        inputValue,
        setOptions,
        minLength
      });
    }
  }, [inputValue, items, promptCreateNewOption]);

  // If the user has selected an item, we'll set the value of the field
  // or if the combobox state has a selected item, we'll set the value to the formik state
  useEffect(() => {
    if (selectedItem) {
      setValue(selectedItem);
    }
  }, [selectedItem]);

  // If the formik state has a value, we'll set the selected item to the combobox state
  useEffect(() => {
    if (value?.value !== selectedItem?.value) {
      selectItem(value);
    }
  }, [value]);
  const parentRef = useRef(null);
  const resetInputValue = () => setInputValue('');
  return /*#__PURE__*/React.createElement("div", _extends({
    id: id,
    className: [baseClassName, componentClassName, userClassName, `y-${color}`].filter(e => e).join(' '),
    style: style,
    ref: parentRef
  }, getComboboxProps()), /*#__PURE__*/React.createElement(FormLabel, _extends({}, getLabelProps(), {
    name: name,
    optional: optional,
    color: labelColor
  }), label), /*#__PURE__*/React.createElement("div", {
    className: "input-container"
  }, /*#__PURE__*/React.createElement("input", _extends({}, getInputProps(), {
    className: "input",
    disabled: disabled,
    placeholder: placeholder
  })), isFetching && /*#__PURE__*/React.createElement(LoadingCircle, {
    className: "x-main"
  }), inputValue.length > minLength && !isFetching && /*#__PURE__*/React.createElement(Button, {
    isSimple: true,
    isCompact: true,
    color: "heading",
    onClick: resetInputValue
  }, /*#__PURE__*/React.createElement("span", {
    className: "icon"
  }, "Y"))), /*#__PURE__*/React.createElement(FormDescription, {
    className: "s-1",
    description: description,
    name: name
  }), /*#__PURE__*/React.createElement(Popover, {
    isOpen: isOpen,
    parentRef: parentRef
  }, /*#__PURE__*/React.createElement(Menu, _extends({
    className: `y-${color}`,
    isOpen: isOpen,
    getItemProps: getItemProps,
    highlightedIndex: highlightedIndex,
    items: items
  }, getMenuProps()))));
};
Combobox.propTypes = {
  /**
   * The HTML id for this element
   */
  id: PropTypes.string,
  /**
   * The HTML class names for this element
   */
  className: PropTypes.string,
  /**
   * The React-written, css properties for this element.
   */
  style: PropTypes.objectOf(PropTypes.string),
  /**
   * The label of the custom select input
   */
  label: PropTypes.string,
  /**
   * The custom select input options from the backend
   */
  options: PropTypes.arrayOf(PropTypes.shape({
    value: PropTypes.string,
    label: PropTypes.string
  })),
  /**
   * The name of the custom select input
   */
  name: PropTypes.string,
  /**
   * The function to fetch the options from the backend
   */
  getOptions: PropTypes.func,
  /**
   * The function to set the value of the custom select input
   */
  setValue: PropTypes.func.isRequired,
  /**
   * The custom select input description
   */
  description: PropTypes.string,
  /**
   * The value of the custom select input
   */
  value: PropTypes.shape({
    value: PropTypes.string,
    label: PropTypes.string
  }),
  /**
   * The base color of the combobox custom select input
   */
  color: PropTypes.string,
  /**
   * Whether the query getting the combobox options is inFlight
   */
  isFetching: PropTypes.bool.isRequired,
  /**
   * The minimum length of the search input to start fetching the options
   */
  minLength: PropTypes.number,
  /**
   * The function to transform the search input
   */
  transformSearch: PropTypes.func,
  /**
   * Whether the input filed shpuld be disabled
   */
  disabled: PropTypes.bool,
  /**
  * The number of columns the label should span
  */
  labelSpan: PropTypes.number,
  /**
   * The number of columns the input should span
   */
  inputSpan: PropTypes.number,
  /**
   * The number of columns the label should span on desktop
   */
  desktopLabelSpan: PropTypes.number,
  /**
   * The number of columns the input should span on desktop
   */
  desktopInputSpan: PropTypes.number,
  /**
  * Whether the input is optional or not
  */
  optional: PropTypes.bool,
  /**
   * The placeholder text for the input
   */
  placeholder: PropTypes.string
};
Combobox.defaultProps = {
  // someProp: false
};
export default Combobox;